import { useEffect, useState } from 'react';
import { getImageWidth } from './getImageWidth';

interface ImageItem {
  id: string;
  imageUrls?: {
    logo?: string;
  } | null;
}

/**
 * Hook to load and cache image widths for a list of items with images
 * @param items - Array of items with id and imageUrls.logo
 * @returns Record mapping item id to image width in pixels
 */
export function useImageWidths<T extends ImageItem>(
  items: Array<T> | null,
): Record<string, number> {
  const [imageWidths, setImageWidths] = useState<Record<string, number>>({});
  const [itemHash, setItemHash] = useState<string>('');

  useEffect(() => {
    if (!items || items.length === 0) return;
    const newHash = getItemHash(items);
    if (newHash === itemHash) return;
    setItemHash(newHash);

    const loadWidths = async () => {
      const widthPromises = items.map(async (item) => {
        const src = item.imageUrls?.logo;
        if (!src) return { id: item.id, width: 0 };
        const width = await getImageWidth(src);
        return { id: item.id, width };
      });

      const results = await Promise.all(widthPromises);
      const widthMap: Record<string, number> = {};
      for (const { id, width } of results) {
        widthMap[id] = width;
      }
      setImageWidths(widthMap);
    };

    loadWidths();
  }, [items]);

  return imageWidths;
}

const getItemHash = <T extends ImageItem>(items: Array<T> | null): string => {
  if (!items || items.length === 0) return '';
  return items
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((item) => `${item.id}:${item.imageUrls?.logo ?? ''}`)
    .join(',');
};
